"use client";

import { ChartWrapper } from "charts/src/components/chart";
import { Range } from "charts/src/types/chart";
import Image from "next/image";
import Link from "next/link";
import { z } from "zod";

import { ErrorView } from "@/components/error-view";
import { links } from "@/data/links";
import { useChartConfigDetail } from "@/features/analysis/services/chart-config-detail";
import { timeFrameSchema } from "@/features/analysis/services/chart-config-list";
import { ChartId, ChartSlug } from "@/features/analysis/types/chart";
import { Lang } from "@/i18n-config";
import { DateOption } from "@/utils/date-options";
import { formatSymbol } from "@/utils/i18n/format-symbol";
import { getTranslations } from "@/utils/i18n/get-translations";

import { LoadingIndicator } from "ui/core/button/LoadingIndicator";

import { apiDataToInstanceData } from "../data-convert/api-data-to-instance-data";
import { apiDataToInstanceDataConfig } from "../data-convert/api-data-to-instance-data-config";
import { apiDataToInstanceLabels } from "../data-convert/api-data-to-instance-labels";
import { apiDataToWatermarkPosition } from "../data-convert/api-data-to-watermark-position";
import {
  DUMMY_RANGE,
  DUMMY_VALUE_FORMAT_TYPE,
  PE_STREAM_CHART_ID,
  WATERMARK_SIZE,
  getChartColorSet,
  getChartIconSrc,
} from "../data/chart-config";

type ChartGridItemProps = {
  lang: Lang;
  symbol: string;
  chartId: ChartId;
  slug: ChartSlug;
  timeFrame: z.infer<typeof timeFrameSchema>[number];
  dateOption?: DateOption;
  limit?: number;
};

export const ChartGridItem = ({
  lang,
  symbol,
  chartId,
  slug,
  timeFrame,
  dateOption,
  limit = 20,
}: ChartGridItemProps) => {
  const isPeStreamChart = chartId === PE_STREAM_CHART_ID;

  const {
    data: chartConfigDetail,
    isError,
    isFetching,
  } = useChartConfigDetail(symbol, chartId, timeFrame, limit);

  const { t } = getTranslations(lang);

  if (isError) {
    return (
      <GridItem body={<ErrorView children={t.error.somethingWentWrong} />} />
    );
  }

  if (!chartConfigDetail || isFetching) {
    return (
      <GridItem
        body={
          <div className="flex h-full w-full items-center justify-center">
            <LoadingIndicator />
          </div>
        }
      />
    );
  }

  const converterInputParams = {
    chartConfigDetail,
    timeFrame,
    chartId,
    symbol,
    lang,
    mode: "grid" as const,
  };

  const instanceData = apiDataToInstanceData(converterInputParams);
  const instanceDataConfig = apiDataToInstanceDataConfig(converterInputParams);
  const instanceLabels = apiDataToInstanceLabels(converterInputParams);
  const watermarkPosition = apiDataToWatermarkPosition(converterInputParams);

  // 非多數據圖表，仍需第一個圖表的 type
  const firstChartType = chartConfigDetail.chartDatas[0]?.chartType ?? "line";
  const colorSet = getChartColorSet(chartId, instanceDataConfig.length);
  const iconSrc = getChartIconSrc(chartId);

  const convertDateOptionToRange = (dateOption?: DateOption): Range => {
    switch (dateOption) {
      case "3Y":
        return "3y";
      case "5Y":
        return "5y";
      case "10Y":
        return "10y";
      default:
        return DUMMY_RANGE;
    }
  };

  const commonChartWrapperProps = {
    isControllable: false,
    valueFormatType: DUMMY_VALUE_FORMAT_TYPE,
    className: "!bg-transparent",
    range: DUMMY_RANGE,
    timeFrame,
    symbols: [symbol] as string[],
    locale: lang,
  } as const;

  const displaySymbol = formatSymbol(symbol);
  const chartInstance = isPeStreamChart ? (
    <ChartWrapper
      {...commonChartWrapperProps}
      chartKey="pe_stream"
      chartType="line"
      range={convertDateOptionToRange(dateOption)}
      customChartOptions={{
        maintainAspectRatio: false,
      }}
      customColorset={colorSet}
    />
  ) : (
    <ChartWrapper
      {...commonChartWrapperProps}
      chartKey="financial_metrices_basic_chart"
      chartType={firstChartType}
      instanceData={instanceData}
      instanceLabels={instanceLabels}
      instanceDataConfig={instanceDataConfig}
      customColorset={colorSet}
    />
  );

  return (
    <>
      <GridItem
        title={
          <>
            <h3 className="font-semibold text-gray-700">
              <span className="flex gap-2">
                <Image
                  src={iconSrc}
                  alt={`${symbol} ${chartConfigDetail.title}`}
                  className="relative top-[1px]"
                  width={24}
                  height={12}
                />
                {`${displaySymbol} ${chartConfigDetail.title}`}
              </span>
            </h3>
            <Link
              href={`/${lang}${links.myAnalysisFinancialMetricsChart({
                symbol,
                slug,
              })}`}
            >
              <Image
                src="/icons/expand.svg"
                width={16}
                height={16}
                alt="expand"
              />
            </Link>
          </>
        }
        body={
          <>
            <Image
              src="/watermark/growin.png"
              className="absolute z-10 opacity-20"
              style={{
                left: watermarkPosition.left,
                top: watermarkPosition.top,
              }}
              alt="Growin"
              width={WATERMARK_SIZE}
              height={WATERMARK_SIZE}
            />
            {chartInstance}
          </>
        }
      />
    </>
  );
};

const ChartGridItemWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="rounded-md bg-white shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
      {children}
    </div>
  );
};

const ChartGridItemBody = ({ children }: { children: React.ReactNode }) => {
  return <div className="relative h-[290px]">{children}</div>;
};

const ChartGridItemTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative z-10 -mb-11 flex items-center justify-between p-5">
      {children}
    </div>
  );
};

const GridItem = ({
  title = "",
  body,
}: {
  title?: React.ReactNode;
  body: React.ReactNode;
}) => {
  return (
    <ChartGridItemWrapper>
      <ChartGridItemTitle>{title}</ChartGridItemTitle>
      <ChartGridItemBody>{body}</ChartGridItemBody>
    </ChartGridItemWrapper>
  );
};
